import { Flash, Section, Text, toast } from '@acast/decibel';
import * as Sentry from '@sentry/browser';
import {
  fetchShow,
  fetchShowDrafts,
  fetchShowEpisodes,
  selectShow,
  selectShowDrafts,
  selectShowDraftsTotal,
  selectShowEpisodes,
  selectShowEpisodesTotal,
} from 'ducks/shows';
import T from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import navigateTo from 'shared/helpers/navigate-to';
import isShowValid from 'validation/show';
import DraftsList from './drafts-list';
import EpisodeAudioFileDropTarget from './episode-audio-file-drop-target';
import EpisodesList from './episodes-list';

const PAGE_SIZE = 25;
const defaultEpisodesQueryParams = {
  limit: PAGE_SIZE,
  skip: 0,
  order: 'publishDate.desc',
};

const defaultDraftsQueryParams = {
  order: 'created.desc',
};

class ShowEpisodesView extends Component {
  static propTypes = {
    show: T.object,
    total: T.number,
    episodes: T.array,
    episodesTotal: T.number,
    drafts: T.array,
    draftsTotal: T.number,
    fetchShow: T.func,
    fetchShowEpisodes: T.func,
    fetchShowDrafts: T.func,
    params: T.shape({
      showId: T.string,
    }).isRequired,
  };

  state = { isLoading: true };

  componentDidMount() {
    this.runFetches([
      this.props.fetchShow(this.props.params.showId),
      this.getEpisodesFetch(this.props),
      this.getDraftsFetch(this.props),
    ]);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (this.props.params.showId !== nextProps.params.showId) {
      this.runFetches([
        this.props.fetchShow(nextProps.params.showId),
        this.getEpisodesFetch(nextProps),
        this.getDraftsFetch(nextProps),
      ]);
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    const isShowSame = this.props.params.showId === nextProps.params.showId;
    const isSameEpisodes = this.props.episodes.length === nextProps.episodes.length;
    const isSameDrafts = this.props.drafts.length === nextProps.drafts.length;
    const isSameLoading = this.state.isLoading === nextState.isLoading;

    return !isShowSame || !isSameEpisodes || !isSameDrafts || !isSameLoading;
  }

  onEpisodeClicked = episodeId => {
    navigateTo(`/episodes/${episodeId}`);
  };

  getEpisodesFetch = props => {
    const queryParams = {
      ...defaultEpisodesQueryParams,
      skip: props.episodes.length,
      limit: PAGE_SIZE,
    };

    return props.fetchShowEpisodes(props.params.showId, queryParams);
  };

  getDraftsFetch = props => props.fetchShowDrafts(props.params.showId, defaultDraftsQueryParams);

  runFetches = fetches => {
    this.setState({
      isLoading: true,
    });
    return Promise.all(fetches)
      .then(() => this.setState({ isLoading: false }))
      .catch(error => {
        toast.danger(`Error fetching episode stats: ${error.message}`, { closable: true });
        Sentry.captureException(error);
        this.setState({ isLoading: false });
      });
  };

  loadNextBatch = () => {
    if (this.state.isLoading) {
      return;
    }

    this.runFetches([this.getEpisodesFetch(this.props)]);
  };

  render() {
    const { show, params, episodes, episodesTotal, drafts, draftsTotal } = this.props;
    const showIsValid = !show || isShowValid(show);
    return (
      <>
        <Text variant="h1">{show.name}</Text>
        <EpisodeAudioFileDropTarget showId={params.showId}>
          {!showIsValid && (
            <Flash
              intent="primary"
              action={
                <Flash.Action onClick={() => navigateTo(`/shows/${params.showId}/settings`)}>
                  Update Settings
                </Flash.Action>
              }
              mb={6}
            >
              <b>ATTENTION:</b> You won't be able to create and publish episodes until you have
              updated your show's settings.
            </Flash>
          )}
          {!!draftsTotal && (
            <Section data-test-id="show-draft-episodes">
              <Text variant="h3">Drafts {draftsTotal !== null && ` (${draftsTotal})`}</Text>
              <DraftsList
                pending={this.state.isLoading}
                drafts={drafts}
                onEpisodeClicked={this.onEpisodeClicked}
              />
            </Section>
          )}
          <Section data-test-id="show-episodes">
            <Text variant="h3">Episodes {episodesTotal !== null && ` (${episodesTotal})`}</Text>
            <EpisodesList
              pending={this.state.isLoading}
              episodesTotal={episodesTotal}
              episodes={episodes}
              loadNextBatch={this.loadNextBatch}
              onEpisodeClicked={this.onEpisodeClicked}
            />
          </Section>
        </EpisodeAudioFileDropTarget>
      </>
    );
  }
}

export default connect(
  (state, { params }) => ({
    show: selectShow(params.showId)(state),
    episodesTotal: selectShowEpisodesTotal(params.showId)(state),
    episodes: selectShowEpisodes(params.showId)(state),
    drafts: selectShowDrafts(params.showId)(state),
    draftsTotal: selectShowDraftsTotal(params.showId)(state),
  }),
  { fetchShowEpisodes, fetchShowDrafts, fetchShow }
)(ShowEpisodesView);
